import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Webcam from "react-webcam";
import { useNavigate } from "react-router-dom";
import { useAtom } from "jotai";
import { useTranslation } from "react-i18next";
import jsQR from "jsqr";
import { usePlayVoiceWithCooldown } from "~/components/hooks/usePlayVoiceWithCooldown";
import { mapErrorCode, buildErrorPath } from "~/utils/error";
import { ROUTE } from "~/constants/routes";
import { languageAtom } from "~/Atom";
import { apiClient, buildIfaceApiUrl, createIfaceHeaders } from "~/utils/apiClient";
import {
  CallType,
  NotificationType,
  ScreenType,
  type ChatRequestType,
  type ReceptionByAppointmentData,
} from "~/utils/receptionDB.types";
import { useInactivityTimeout } from "~/utils/receptionUtils";
import ReceptionCommunicatingIndicator from "~/components/ReceptionCommunicatingIndicator";
import "~/css/reception/ReceptionQrScanScreen.css";

const INVALID_CALL_TYPE = "R9996";
const INVALID_CHAT_STATUS = "R9997";
const NO_CALL_DESTINATION = "R2009";
const NOTIFICATION_DECODE_ERROR = "R104";

type ReceptionDisplayState = {
  groupName?: string;
  userName?: string;
};

type ChatCompleteNavigationState = ReceptionDisplayState;

/** Navigation state passed to /reception/calling */
type RingUpPhoneNavigationState = ReceptionDisplayState & {
  chatRequestType: ChatRequestType;
  callType?: string;
  extensionNumber?: string;
  faceTimeAddress?: string;
  appointmentId?: number;
  appointmentVisitorIds?: number[];
};

const SCAN_PROCESS_DELAY_MS = 500;
const QR_SCAN_VIDEO_CONSTRAINTS = { facingMode: "user" } as const;

const normalizeApiTypeValue = (value: unknown): string => {
  if (typeof value !== "string") return "";
  return value.trim().toUpperCase();
};

const toChatRequestType = (isChatSuccess: boolean): ChatRequestType =>
  isChatSuccess ? "success" : "failed";

const resolveLocalizedAppointmentText = <T extends { languageCode?: string }>(
  items: T[] | null | undefined,
  languageCode: string,
  getText: (item: T) => string | null | undefined
): string | undefined => {
  if (!items?.length) return undefined;

  const normalizedLanguage = languageCode.toLowerCase();
  const localizedItem =
    items.find((item) => {
      const code = item.languageCode?.toLowerCase();
      return !!code && normalizedLanguage.startsWith(code);
    }) ?? items[0];
  const text = getText(localizedItem)?.trim();
  return text ? text : undefined;
};

const stopWebcamStream = (webcam: Webcam | null) => {
  const video = webcam?.video;
  if (!video) return;
  const stream = video.srcObject as MediaStream | null;
  stream?.getTracks().forEach((t) => t.stop());
  video.srcObject = null;
};

const readQrValueFromCanvas = (canvas: HTMLCanvasElement): string | null => {
  const context = canvas.getContext("2d");
  if (!context) return null;

  const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
  const result = jsQR(imageData.data, imageData.width, imageData.height);
  return result?.data?.trim() || null;
};

/**
 * 受付用のQR読み取り画面コンポーネント。
 * カメラ映像からQRコードを検出し、予約APIの結果に応じて次画面へ遷移する。
 *
 */
const ReceptionQrScanScreen: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const webcamRef = useRef<Webcam | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const scanningLockRef = useRef(false);
  const [language] = useAtom(languageAtom);
  const { playWithCooldown } = usePlayVoiceWithCooldown({ cooldownMs: 2000 });

  const { stopTimer } = useInactivityTimeout(navigate);

  const byAppointmentUrl = useMemo(() => buildIfaceApiUrl("v1/receptions/by-appointment"), []);

  const navigateForward = useCallback(
    (path: string, state?: RingUpPhoneNavigationState | ChatCompleteNavigationState) => {
      navigate(path, { state });
    },
    [navigate]
  );

  const navigateError = useCallback(
    (uiCode: string) => {
      navigate(buildErrorPath(uiCode));
    },
    [navigate]
  );

  // --- Voice playback ---
  useEffect(() => {
    void playWithCooldown(t("receptionQrScan.title")).catch((error) => {
      console.error("qrScan playVoice failed:", error);
    });
  }, [playWithCooldown]);

  const navigateCallProcess = useCallback(
    (data: ReceptionByAppointmentData, chatRequestType: ChatRequestType) => {
      const state: RingUpPhoneNavigationState = {
        groupName: resolveLocalizedAppointmentText(
          data.groupNames,
          language,
          (item) => item.groupName
        ),
        userName: resolveLocalizedAppointmentText(data.names, language, (item) => item.name),
        chatRequestType,
        callType: data.callType ?? undefined,
        extensionNumber: data.extensionNumber ?? undefined,
        faceTimeAddress: data.faceTimeAddress ?? undefined,
        appointmentId: data.appointmentId,
        appointmentVisitorIds: data.appointmentVisitorIds,
      };
      navigateForward(ROUTE.RECEPTION.CALLING, state);
    },
    [language, navigateForward]
  );

  // --- Second-level call destination validation ---
  const validateCallProcess = useCallback(
    (data: ReceptionByAppointmentData): boolean => {
      const callType = normalizeApiTypeValue(data.callType);
      if (!callType) {
        navigateError(INVALID_CALL_TYPE);
        return false;
      }

      if (callType === CallType.Phone) {
        if (!data.extensionNumber?.trim()) {
          navigateError(NO_CALL_DESTINATION);
          return false;
        }
        return true;
      }

      if (callType === CallType.FaceTime) {
        navigateError(NO_CALL_DESTINATION);
        return false;
      }

      navigateError(INVALID_CALL_TYPE);
      return false;
    },
    [navigateError]
  );

  const handleCallAndChat = useCallback(
    (data: ReceptionByAppointmentData) => {
      if (data.isChatSuccess === undefined || data.isChatSuccess === null) {
        navigateError(INVALID_CHAT_STATUS);
        return;
      }

      if (!validateCallProcess(data)) {
        return;
      }

      // Convert isChatSuccess bool to ChatRequestType
      navigateCallProcess(data, toChatRequestType(data.isChatSuccess));
    },
    [navigateCallProcess, navigateError, validateCallProcess]
  );

  // --- Navigation routing based on notificationType (mirrors iOS ReadReceptionQRPresenter) ---
  const navigateByNotificationType = useCallback(
    (notificationType: string, data: ReceptionByAppointmentData) => {
      switch (notificationType) {
        case NotificationType.Chat:
          navigateForward(ROUTE.RECEPTION.COMPLETE, {
            groupName: resolveLocalizedAppointmentText(
              data.groupNames,
              language,
              (item) => item.groupName
            ),
            userName: resolveLocalizedAppointmentText(data.names, language, (item) => item.name),
          });
          return;
        case NotificationType.Call:
          if (!validateCallProcess(data)) {
            return;
          }
          navigateCallProcess(data, "noRequest");
          return;
        case NotificationType.ChatAndCall:
          handleCallAndChat(data);
          return;
        default:
          navigateError(NOTIFICATION_DECODE_ERROR);
      }
    },
    [
      handleCallAndChat,
      language,
      navigateCallProcess,
      navigateError,
      navigateForward,
      validateCallProcess,
    ]
  );

  // --- Appointment flow ---
  const handleAppointmentFlow = useCallback(
    async (qrCode: string) => {
      try {
        setIsProcessing(true);
        // Stop auto-return timer while API is in progress
        stopTimer();

        const response = await apiClient.post<{ data?: ReceptionByAppointmentData }>(
          byAppointmentUrl,
          { qrCode },
          { headers: createIfaceHeaders(true) }
        );

        const data = response?.data?.data;
        if (!data) {
          navigateError(mapErrorCode(ScreenType.QR_SCAN));
          return;
        }
        const notificationType = normalizeApiTypeValue(data.notificationType);

        navigateByNotificationType(notificationType, data);
      } catch (error) {
        const maybeError = error as { response?: { data?: { errorCode?: unknown } } } | undefined;
        const rawCode = maybeError?.response?.data?.errorCode;
        const backendCode = typeof rawCode === "number" ? rawCode : undefined;
        const uiCode = mapErrorCode(ScreenType.QR_SCAN, backendCode);

        navigateError(uiCode);
      } finally {
        setIsProcessing(false);
        scanningLockRef.current = false;
      }
    },
    [byAppointmentUrl, navigateByNotificationType, navigateError, stopTimer]
  );

  // --- QR scan result handler (no client-side format validation; server validates) ---
  const handleScanResult = useCallback(
    (raw: string) => {
      const qrCode = raw.trim();
      if (!qrCode) return;
      void handleAppointmentFlow(qrCode);
    },
    [handleAppointmentFlow]
  );

  // --- Camera frame capture ---
  const captureFrame = useCallback((): HTMLCanvasElement | null => {
    const video = webcamRef.current?.video;
    if (!video || video.readyState < 2) return null;

    const canvas = canvasRef.current;
    if (!canvas) return null;
    const context = canvas.getContext("2d");
    if (!context) return null;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    return canvas;
  }, []);

  const processDetectedCode = useCallback(
    async (value: string, stoppedRef: { current: boolean }) => {
      scanningLockRef.current = true;
      stopWebcamStream(webcamRef.current);

      // 0.5s delay before processing
      await new Promise((r) => setTimeout(r, SCAN_PROCESS_DELAY_MS));
      if (!stoppedRef.current) {
        handleScanResult(value);
      }
    },
    [handleScanResult]
  );

  // --- Camera scanning loop ---
  useEffect(() => {
    const stoppedRef = { current: false };

    const scan = async () => {
      if (stoppedRef.current || scanningLockRef.current || isProcessing) return;

      const source = captureFrame();
      if (!source) return;

      try {
        const value = readQrValueFromCanvas(source);
        if (!value) return;

        await processDetectedCode(value, stoppedRef);
      } catch (error) {
        console.error("QR_SCAN_DETECT_ERROR:", error);
      }
    };

    const intervalId = window.setInterval(() => {
      void scan();
    }, 600);

    return () => {
      stoppedRef.current = true;
      window.clearInterval(intervalId);
    };
  }, [captureFrame, isProcessing, processDetectedCode]);

  return (
    <>
      <div className="reception-qr-scan-screen__content">
        <h2 className="reception-qr-scan-screen__title">{t("receptionQrScan.title")}</h2>

        <div className="reception-qr-scan-screen__qr-camera-wrap">
          <Webcam
            ref={webcamRef}
            className="reception-qr-scan-screen__qr-camera"
            audio={false}
            mirrored
            screenshotFormat="image/jpeg"
            videoConstraints={QR_SCAN_VIDEO_CONSTRAINTS}
          />
          <div className="reception-qr-scan-screen__qr-camera-guide" />
        </div>

        {isProcessing ? <ReceptionCommunicatingIndicator /> : null}
      </div>

      <canvas ref={canvasRef} style={{ display: "none" }} />
    </>
  );
};

export default ReceptionQrScanScreen;
